import { defineCollection } from "@rebasepro/cms-types";

const tiersCollection = defineCollection({
    name: "Tiers",
    singularName: "Tier",
    slug: "tiers",
    table: "tiers",
    description: "Configurable loyalty tier levels. Each tier defines a points threshold, discount percentage, and integration mappings for Shopify tags and Odoo pricelists.",
    history: true,
    securityRules: [
        { operation: "select", access: "public" },
        { operations: ["insert", "update", "delete"], roles: ["admin"] }
    ],
    callbacks: {
        // A tier's name, discount, colour and card image are on every card of
        // that tier: Google rewrites the levels' classes, Apple's phones
        // download their cards again. Same in-process route as the card pushes
        // (backend/functions/wallet.ts); fire-and-forget, as they are.
        afterSave: async ({ values }) => {
            const row = values as Record<string, unknown>;
            const savedAt = row.updated_at ?? row.updatedAt ?? row.created_at ?? row.createdAt;
            const savedDate = savedAt ? new Date(savedAt as string) : null;
            const since = savedDate && !Number.isNaN(savedDate.getTime()) ? savedDate.toISOString() : undefined;
            type TierRefresh = (since?: string) => Promise<unknown>;
            const walletPush = (globalThis as Record<symbol, unknown>)[Symbol.for("unfeigned-loyalty.wallet-push")] as
                Record<string, TierRefresh | undefined> | undefined;
            walletPush?.tiers?.(since).catch((err) => {
                console.error("[Wallet] Failed to update the cards after a tier change:", err);
            });
        }
    },
    properties: {
        id: {
            name: "ID",
            type: "number",
            isId: "increment"
        },
        name: {
            name: "Tier Name",
            type: "string",
            validation: { required: true, unique: true },
            description: "Display name shown in the admin panel and customer communications"
        },
        slug: {
            name: "Slug",
            type: "string",
            validation: { required: true, unique: true, matches: /^[a-z0-9_-]+$/, matchesMessage: "Only lowercase letters, numbers, hyphens and underscores" },
            description: "Internal identifier (e.g. 'gold', 'platinum')"
        },
        kind: {
            name: "Kind",
            type: "string",
            defaultValue: "points",
            description: "points: reached and kept on points (INSIDER, CORE, MODERN MIND). manual: given and " +
                "taken off by hand, no points needed, no expiry (Family & Friends). offer: the second-purchase " +
                "discount, given for 30 days after a member's first purchase in place of the entry level's.",
            enum: [
                { id: "points", label: "On points", color: "blue" },
                { id: "manual", label: "By hand", color: "purple" },
                { id: "offer", label: "Second purchase", color: "orange" }
            ]
        },
        points_threshold: {
            name: "Points Threshold",
            type: "number",
            validation: { required: true, min: 0, integer: true },
            description: "Points of the last 12 months that reach this tier (1€ = 1 point). Ignored for a tier given by hand or the second-purchase offer."
        },
        discount_percentage: {
            name: "Discount %",
            type: "number",
            validation: { required: true, min: 0, max: 100 },
            description: "Percentage discount applied in Shopify checkout and Odoo POS pricelist"
        },
        color: {
            name: "Color",
            type: "string",
            defaultValue: "#6B7280",
            description: "Badge color for admin panel and wallet pass design"
        },
        text_color: {
            name: "Text colour",
            type: "string",
            validation: { matches: /^#[0-9a-fA-F]{6}$/, matchesMessage: "A colour as #rrggbb, e.g. #C14E55" },
            description: "The text on the Apple card, when it should not follow the card colour (e.g. red on " +
                "white). Google chooses its own text colour."
        },
        wallet_image_url: {
            name: "Card image",
            type: "string",
            url: true,
            admin: { urlPreview: "image" },
            description: "The tier's image on the Apple Wallet card, across it behind the tier name (the stripes). " +
                "A PNG on HTTPS; optional."
        },
        sort_order: {
            name: "Sort Order",
            type: "number",
            validation: { required: true, integer: true },
            defaultValue: 0,
            description: "Display ordering (lower = shown first)"
        },
        shopify_tag: {
            name: "Shopify Tag",
            type: "string",
            validation: { unique: true },
            description: "Tag pushed to Shopify customer profile (e.g. 'loyalty_gold'). Used by Shopify Functions for auto-discount."
        },
        odoo_pricelist_id: {
            name: "Odoo Pricelist ID",
            type: "number",
            validation: { integer: true },
            description: "Odoo pricelist ID mapped to this tier for native POS pricing"
        },
        odoo_tag_id: {
            name: "Odoo Tag ID",
            type: "number",
            validation: { integer: true },
            description: "Odoo partner category ID for this tier's loyalty tag"
        },
        created_at: {
            name: "Created At",
            type: "date",
            mode: "date_time",
            autoValue: "on_create",
            admin: { readOnly: true, hideFromCollection: true }
        },
        updated_at: {
            name: "Updated At",
            type: "date",
            mode: "date_time",
            autoValue: "on_update",
            admin: { readOnly: true, hideFromCollection: true }
        }
    },
    admin: {
        icon: "Crown",
        group: "Loyalty",
        defaultViewMode: "list",
        enabledViews: ["list", "table"],
        // Four rows that exist to answer 'what earns what' — the threshold and the
        // discount are the whole content, and the Odoo pricelist is the id every
        // import and sync is checked against.
        listProperties: ["kind", "points_threshold", "discount_percentage", "odoo_pricelist_id"],
        previewProperties: ["name"],
        openEntityMode: "side_panel",
        inlineEditing: true,
        exportable: true,
        sort: ["sort_order", "asc"],
        propertiesOrder: [
            "name", "slug", "kind", "points_threshold", "discount_percentage",
            "color", "text_color", "wallet_image_url", "sort_order", "shopify_tag", "odoo_pricelist_id", "odoo_tag_id",
            "created_at", "updated_at"
        ]
    }
});

export default tiersCollection;
